import React, { Fragment, useMemo } from 'react';

import { getReviewsSchema, ReviewItem } from '@zola-helpers/client/dist/es/schema';
import { SearchLocationReviewView } from '@zola/svc-marketplace-ts-types';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import Schemas from '@zola/zola-ui/src/components/Schemas/Schemas';
import StarRatings from '@zola/zola-ui/src/components/StarRatings/StarRatings';
import {
  LeftButton,
  RightButton,
  TileScroller,
  TilesWrapper,
} from '@zola/zola-ui/src/components/TileScroller';
import { ViewMore } from '@zola/zola-ui/src/components/ViewMore/ViewMore';
import { COLORS3, FONT } from '@zola/zola-ui/src/styles/emotion';
import { COLORS_SEMANTIC } from '@zola/zola-ui/src/styles/emotion/colors3';
import { Title2 } from '@zola/zola-ui/src/typography/Headings';
import { BodySmall } from '@zola/zola-ui/src/typography/Paragraphs';

import styled from '@emotion/styled';

import { isSearchLocation } from '~/util/searchUtils';

import { useSearchResultsPageContext } from '../../contexts/SearchResultsPageContext';

/**
 * Force the title to a specific width, then let it grow to whatever height is
 * needed.
 */
const Tile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: flex-start;
  padding: 24px;
  white-space: wrap;
  flex-shrink: 0;
  background-color: ${COLORS3.WHITE_100};
  border-radius: 16px;
  width: 220px;

  font-size: 14px;
  line-height: 20px;
`;

/**
 * Stars + text body, just a a 16px gap between.
 */
const TileContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  button {
    font-weight: ${FONT.FONT_WEIGHT_REGULAR};
  }
`;

const LocationAndRating = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReviewerInfo = styled(BodySmall)`
  font-weight: ${FONT.FONT_WEIGHT_SEMI_BOLD};
`;

/**
 * A Review for this search location.  This is missing title and the couple's
 * location from the designs as that data was not initially returned.
 */
const ReviewTile = ({ review }: { review: SearchLocationReviewView }) => {
  return (
    <Tile>
      <LocationAndRating>
        <ReviewerInfo>{review.reviewerName}</ReviewerInfo>
        <StarRatings
          activeStars={review.overallRating}
          showReviewCount={false}
          totalStars={5}
          reviewCount={''}
          fillColor={COLORS_SEMANTIC.POSITIVE_100}
          starSize="large"
        />
      </LocationAndRating>
      <TileContent>
        <ViewMore
          lines={7}
          backgroundColor={COLORS3.WHITE_100}
          viewLess={() => <Fragment />}
          viewMore={({ setIsOpen }) => (
            <LinkV2
              sizes="smaller"
              role="button"
              noTextTransform
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsOpen(true);
              }}
            >
              Read more
            </LinkV2>
          )}
        >
          {review.reviewText}
        </ViewMore>
      </TileContent>
    </Tile>
  );
};

/**
 * Add some custom presentation to the tile scroller.  Most of these can be found
 * as examples in the storybook
 *
 * - Center the cards on the page
 * - Adjust padding and gap
 * - Color the buttons white with borders
 */
const StyledTileScroller = styled(TileScroller)`
  justify-content: center;
  ${TilesWrapper} {
    min-width: initial; // This lets us center, see storybook for example

    // This is padding and gap
    padding-top: 0;
    padding-bottom: 16px;
    gap: 16px;
  }

  ${LeftButton}, ${RightButton} {
    background-color: ${COLORS3.WHITE_100};
    color: ${COLORS3.BLACK_100};
    border: 1px solid ${COLORS3.BLACK_100};
  }
`;

/**
 * Put a gray background around the reviews
 */
const Container = styled.div`
  background-color: ${COLORS3.CLOUD_100};
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

/** Center the review title element */
const StyledTitle = styled(Title2)`
  text-align: center;
`;

/** Prevent the location string from splitting to multiple lines */
const NoWrap = styled.span`
  white-space: nowrap;
`;

/**
 * Zola reviews for a search location.
 */
export const SearchLocationReviews = (props: React.HTMLAttributes<HTMLElement>) => {
  const { searchLocationReviews, vendorSearch } = useSearchResultsPageContext();

  const { selectedLocation } = vendorSearch;

  const location = useMemo(
    () => (isSearchLocation(selectedLocation) ? selectedLocation.label : undefined),
    [selectedLocation]
  );
  const schema = useMemo(() => {
    const reviews: ReviewItem[] = (searchLocationReviews?.reviews || []).map((review) => {
      return {
        reviewBody: review.reviewText,
        author: {
          '@type': 'Person',
          name: review.reviewerName,
        },
        itemReviewed: {
          '@type': 'Organization',
          name: 'Zola Vendor Marketplace',
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: review.overallRating,
          bestRating: 5,
          worstRating: 1,
          author: {
            '@type': 'Person',
            name: review.reviewerName,
          },
        },
      };
    });
    return {
      ...getReviewsSchema(reviews),
      description: `Top reviews from Zola Couples${location ? ` in ${location}` : ''}`,
    };
  }, [location, searchLocationReviews]);

  if (searchLocationReviews && (searchLocationReviews.reviews || []).length > 0) {
    return (
      <Container {...props}>
        <Schemas schemas={schema} />
        <StyledTitle presentation="h5">
          Top reviews from Zola couples{location ? <NoWrap>&nbsp;in {location}</NoWrap> : ''}
        </StyledTitle>
        <StyledTileScroller scrollCenter>
          {(searchLocationReviews?.reviews || []).map((review, index) => (
            <ReviewTile key={index} review={review} />
          ))}
        </StyledTileScroller>
      </Container>
    );
  }
  return <Fragment />;
};
